import { useRef, useState, useEffect } from 'react';
import { ArrowUp, Loader2, Paperclip, Plus, X } from 'lucide-react';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { processSelectedFiles, resetFileInput, IMAGE_ACCEPT } from '@/lib/photoInputUtils';
import { useVisualViewportInset } from '@/hooks/useVisualViewportInset';

interface ChatComposerProps {
	disabled?: boolean;
	placeholder?: string;
	allowAttachments?: boolean;
	/** Storage bucket to upload to. Defaults to 'contact-attachments'. */
	bucket?: string;
	/** Optional path prefix prepended to the submission folder (used for chat-attachments which requires the conversation id). */
	pathPrefix?: string;
	onSend: (payload: { body: string; attachmentPaths: string[] }) => Promise<void> | void;
	/** When provided, replaces the paperclip with a `+` that opens the parent's action sheet. */
	onPlusClick?: () => void;
	/** Lets the parent imperatively trigger the file picker (e.g. from the action sheet). */
	registerFileTrigger?: (trigger: () => void) => void;
}

const MAX_ATTACHMENTS = 3;
const MAX_BYTES = 5 * 1024 * 1024;

export const ChatComposer = ({
	disabled,
	placeholder = 'Type a message…',
	allowAttachments = true,
	bucket = 'contact-attachments',
	pathPrefix,
	onSend,
	onPlusClick,
	registerFileTrigger,
}: ChatComposerProps) => {
	const [body, setBody] = useState('');
	const [attachments, setAttachments] = useState<File[]>([]);
	const [busy, setBusy] = useState(false);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const fileRef = useRef<HTMLInputElement>(null);
	const { keyboardHeight } = useVisualViewportInset();

	useEffect(() => {
		registerFileTrigger?.(() => fileRef.current?.click());
	}, [registerFileTrigger]);

	useEffect(() => {
		const ta = textareaRef.current;
		if (!ta) return;
		ta.style.height = 'auto';
		ta.style.height = Math.min(ta.scrollHeight, 140) + 'px';
	}, [body]);

	const addFiles = async (files: FileList | null) => {
		const slots = MAX_ATTACHMENTS - attachments.length;
		if (slots <= 0) {
			toast.error(`Up to ${MAX_ATTACHMENTS} attachments`);
			resetFileInput(fileRef.current);
			return;
		}
		const processed = await processSelectedFiles(files, { maxCount: slots, multiple: true });
		const sized = processed.filter((f) => {
			if (f.size > MAX_BYTES) {
				toast.error(`${f.name} is over 5MB`);
				return false;
			}
			return true;
		});
		if (sized.length > 0) setAttachments((p) => [...p, ...sized]);
		resetFileInput(fileRef.current);
	};

	const removeAttachment = (i: number) =>
		setAttachments((p) => p.filter((_, idx) => idx !== i));

	const canSend = !disabled && !busy && body.trim().length > 0;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!canSend) return;
		setBusy(true);
		try {
			// upload attachments
			let paths: string[] = [];
			if (attachments.length > 0) {
				const submissionId = crypto.randomUUID();
				const prefix = pathPrefix ? `${pathPrefix.replace(/\/+$/, '')}/` : '';
				const { resizeImageIfImage } = await import('@/lib/imageResizeUtils');
				const uploads = await Promise.all(
					attachments.map(async (file, idx) => {
						const compressed = await resizeImageIfImage(file);
						const safeName = compressed.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80);
						const path = `${prefix}${submissionId}/${idx}-${safeName}`;
						const { error } = await supabase.storage
							.from(bucket)
							.upload(path, compressed, { contentType: compressed.type || 'image/jpeg', upsert: false });
						return error ? null : path;
					})
				);
				paths = uploads.filter((p): p is string => !!p);
			}
			await onSend({ body: body.trim(), attachmentPaths: paths });
			setBody('');
			setAttachments([]);
		} finally {
			setBusy(false);
		}
	};

	return (
		<form
			onSubmit={handleSubmit}
			className="shrink-0 bg-background shadow-[0_-1px_0_rgba(255,255,255,0.04)]"
			style={{ paddingBottom: keyboardHeight }}
		>


			{attachments.length > 0 && (
				<div className="flex gap-2 px-3 pt-2.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
					{attachments.map((f, i) => (
						<div
							key={i}
							className="relative h-14 w-14 shrink-0 rounded-lg overflow-hidden bg-white/[0.05] border border-white/[0.08]"
						>
							<img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
							<button
								type="button"
								onClick={() => removeAttachment(i)}
								className="absolute top-0.5 right-0.5 h-4 w-4 rounded-full bg-black/70 grid place-items-center"
								aria-label="Remove"
							>
								<X className="h-2.5 w-2.5 text-white" />
							</button>
						</div>
					))}
				</div>
			)}
			<div className="flex items-end gap-2 px-3 py-2.5">
				{allowAttachments && (
					<>
						{/* Visually hidden (NOT display:none) — iOS Safari can drop .click()
						    on display:none inputs, which is why the paperclip felt flaky. */}
						<input
							ref={fileRef}
							type="file"
							accept={IMAGE_ACCEPT}
							multiple
							tabIndex={-1}
							aria-hidden="true"
							className="absolute h-px w-px overflow-hidden opacity-0 pointer-events-none -z-10"
							onChange={(e) => addFiles(e.target.files)}
						/>
						<button
							type="button"
							onPointerDown={(e) => {
								// Fire the picker synchronously in the gesture handler so iOS
								// keeps the user-gesture context. onClick alone is fine on
								// most browsers but pointerdown is more reliable on iOS.
								if (onPlusClick) return;
								if (disabled || attachments.length >= MAX_ATTACHMENTS) return;
								// Only main button / primary touch
								if (e.pointerType === 'mouse' && e.button !== 0) return;
								e.preventDefault();
								fileRef.current?.click();
							}}
							onClick={() => {
								if (onPlusClick) onPlusClick();
							}}
							disabled={disabled || attachments.length >= MAX_ATTACHMENTS}
							aria-label={onPlusClick ? 'Quick actions' : 'Attach photo'}
							className="h-11 w-11 shrink-0 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-white/[0.05] active:bg-white/[0.08] active:scale-95 transition-all disabled:opacity-40 touch-manipulation select-none -ml-1"
						>
							{onPlusClick ? <Plus className="h-5 w-5" /> : <Paperclip className="h-[18px] w-[18px]" />}
						</button>
					</>
				)}
				<textarea
					ref={textareaRef}
					value={body}
					onChange={(e) => setBody(e.target.value)}
					placeholder={placeholder}
					disabled={disabled}
					rows={1}
					className="flex-1 resize-none bg-white/[0.04] border border-white/[0.06] focus:border-primary/40 focus:bg-white/[0.06] rounded-2xl px-4 py-2.5 text-[14px] text-foreground placeholder:text-muted-foreground/60 outline-none transition-colors min-h-[40px] max-h-[140px]"
					style={{ fontSize: 16 }}
					onKeyDown={(e) => {
						if (e.key === 'Enter' && !e.shiftKey) {
							e.preventDefault();
							handleSubmit(e as any);
						}
					}}
				/>
				<button
					type="submit"
					disabled={!canSend}
					aria-label="Send"
					className={cn(
						'h-10 w-10 shrink-0 rounded-full grid place-items-center transition-all',
						canSend
							? 'bg-primary text-primary-foreground active:scale-90 shadow-md shadow-primary/30'
							: 'bg-white/[0.08] text-muted-foreground/60'
					)}
				>
					{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" strokeWidth={2.5} />}
				</button>
			</div>
		</form>
	);
};

export default ChatComposer;
